import {
  Component,
  computed,
  inject,
  Signal,
  
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { take } from 'rxjs';

import { CalendarEvent, DATETIMEFORMART , UnleashFeatureFlags} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { PlacesService } from 'src/app/services/placesService/places.service';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { CalendarViewModalComponent } from 'src/app/shared/calendar-view-modal/calendar-view-modal.component';
import { DailyCalendarModalPage } from 'src/app/modals/daily-calendar-modal/daily-calendar-modal.page';
import { EventComponent } from 'src/app/shared/event/event.component';
import { ViewEditBookingComponent } from 'src/app/shared/view-edit-booking/view-edit-booking.component';
import { UnleashService } from 'src/app/services/unleash/unleash.service';


@Component({
  selector: 'app-bookings-widget',
  templateUrl: './bookings-widget.component.html',
  styleUrls: ['./bookings-widget.component.scss'],
})
export class BookingsWidgetComponent {
  protected bookingsStore = inject(BookingsStore);
  private dateTimeFormatService = inject(DatetimeFormatService);
  private modalCtrl = inject(ModalController);
private unleashService = inject(UnleashService);

  loading = this.bookingsStore.loading;
  locale = this.dateTimeFormatService.selectedDateTimeFormat;

  // Computed signals that depend on bookings data
  firstThreeMeetings: Signal<CalendarEvent[]> = computed(() =>
    this.getFirstThreeMeetings(this.bookingsStore.allBookings())
  );
  closestMeeting: Signal<any> = computed(() =>
    this.getClosestMeeting(this.bookingsStore.allBookings())
  );
  remainingTime: Signal<string | null> = computed(() =>
    this.calculateRemainingTime(this.closestMeeting())
  );
  nextMeetingDateTime: Signal<string | null> = computed(() =>
    this.calculateNextMeetingDateTime(this.closestMeeting())
  );

  // Computed signals for current day (updates in real-time)
  currentDayName = computed(() => moment().toDate());
  currentDayNumber = computed(() => moment().format('D'));

  private getFirstThreeMeetings(bookings: CalendarEvent[]): CalendarEvent[] {
    const todayStart = moment().startOf('day').utc();
    const todayEnd = moment().endOf('day').utc();

    // Get first 3 meetings for today
    const todayMeetings = bookings
      .filter((booking) => {
        if (booking.isExpired) {
          return false;
        }
        const bookingStartTime = moment(booking.startTime).utc();
        return bookingStartTime.isBetween(todayStart, todayEnd);
      })
      .sort((a, b) =>
        moment(a.startTime).utc().diff(moment(b.startTime).utc())
      );

    return todayMeetings.slice(0, 3);
  }

  private getClosestMeeting(bookings: CalendarEvent[]): any {
    const now = moment().utc();
    let closestMeetingTime = null;
    let closestMeeting = null;

    // Find the closest upcoming meeting
    bookings.forEach((booking) => {
      if (booking.isExpired) {
        return;
      }
      const bookingStartTime = moment(booking.startTime).utc();

      if (
        bookingStartTime.isAfter(now) &&
        (!closestMeetingTime || bookingStartTime.isBefore(closestMeetingTime))
      ) {
        closestMeetingTime = bookingStartTime;
        closestMeeting = booking;
      }
    });

    return closestMeeting;
  }

  private calculateRemainingTime(closestMeeting: any): string | null {
    if (!closestMeeting) {
      return null;
    }

    const todayStart = moment().startOf('day').utc();
    const todayEnd = moment().endOf('day').utc();
    const now = moment().utc();
    const closestMeetingTime = moment(closestMeeting.startTime).utc();

    if (closestMeetingTime.isAfter(now)) {
      const duration = moment.duration(closestMeetingTime.diff(now));

      // Check if the meeting is today
      if (closestMeetingTime.isBetween(todayStart, todayEnd)) {
        // Calculate remaining time for meetings today
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        } else {
          return `${minutes}m`;
        }
      }
    }

    return null;
  }

  private calculateNextMeetingDateTime(closestMeeting: any): string | null {
    if (!closestMeeting) {
      return null;
    }

    const todayStart = moment().startOf('day').utc();
    const todayEnd = moment().endOf('day').utc();
    const now = moment().utc();
    const closestMeetingTime = moment(closestMeeting.startTime).utc();

    if (closestMeetingTime.isAfter(now)) {
      // Check if the meeting is today
      if (closestMeetingTime.isBetween(todayStart, todayEnd)) {
        // For meetings today, don't show date/time
        return null;
      } else {
        // For future meetings, show the date and time
        return closestMeetingTime.toISOString();
      }
    }

    return null;
  }

  
async openMeetingDetails(meeting: CalendarEvent) {
  if (this.unleashService.isEnabled(UnleashFeatureFlags.BOOKING_CHANGE_DESIGN)) {
    const modal: HTMLIonModalElement = await this.modalCtrl.create({
      component: ViewEditBookingComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      handle: false,
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        attendees: meeting.attendees,
        place: meeting.place,
        meetingToEdit: meeting,
      },
    });
    await modal.present();
  } else {
    const modal: HTMLIonModalElement = await this.modalCtrl.create({
      component: EventComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      handle: false,
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        finalChoosenAttendees: meeting.attendees,
        place: meeting.place,
        purpose: 'readOnlyBooking',
        meetingToEdit: meeting,
      },
    });
    await modal.present();
  }
}

  openCalendarModal() {
    this.modalCtrl
      .create({
        component: CalendarViewModalComponent,
        handle: false,
        cssClass: 'calendar-modal',
      })
      .then((modal) => {
        modal.present();
      });
  }

  async showCalendar() {
    const modal = await this.modalCtrl.create({
      component: DailyCalendarModalPage,
      // initialBreakpoint: 0.99,
      // breakpoints: [0, 0.99],
      // handle: true,
      // canDismiss: true,
      cssClass: 'daily-calendar-modal',
      componentProps: {
        events: this.bookingsStore.allBookingsRaw,
      },
    });

    return await modal.present();
  }
}
